import { forwardRef, useMemo, useState } from 'react';

import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useSelector } from 'react-redux';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';

import DateText from '@/components/DateText';
import Loader from '@/components/Loader';
import Tooltip from '@/components/Tooltip';
import useUserDetails from '@/hooks/useUserDetails';
import { mockDhData } from '@/pages/DataHub/mock/dhData';
import ColumnHeader from '@/pages/DataHub/Preview/ColumnHeader';
import { separator } from '@/pages/DataHub/Preview/constants';
import { formatNumberWithPrecision } from '@/utils/formatNumberWithPrecision';
import { RootState } from 'store';

import PreviewTooltipContent from './PreviewTooltipContent';
import useDataHubPreview from './useDataHubPreview';
import { DhDataItem } from '../DataHub.types';

const VirtuosoTableComponents: TableComponents<
  Record<
    string,
    | string
    | Record<
        string,
        {
          value: string;
          PeriodAbs: string;
          FactsheetVersion: string;
        }
      >
  >
> = {
  Scroller: forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer
      component={Paper}
      sx={{ overflow: 'auto', height: '100%', width: '100%' }}
      {...props}
      ref={ref}
    />
  )),
  Table: props => (
    <Table
      {...props}
      sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }}
    />
  ),
  TableHead: forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead
      {...props}
      ref={ref}
      sx={{ '& .MuiTableCell-root': { verticalAlign: 'bottom' } }}
    />
  )),
  TableRow,
  TableBody: forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody
      {...props}
      ref={ref}
    />
  ))
};
const generateSymbolKey = ({ symbol, metadata }: DhDataItem): string => {
  return [
    symbol.TimeRef,
    symbol.Period,
    symbol.PeriodType,
    symbol.Code,
    metadata.Title,
    metadata.Alias,
    metadata.Currency,
    metadata.Units
  ].join(separator);
};

const DataHubPreview = ({ isSummary }: { isSummary?: boolean }) => {
  const { dhData, loading } = useSelector((state: RootState) => state.dhData);
  const { isUserPremium, isUserExpired } = useUserDetails();
  const flattenedDhData = dhData.toReversed().flat();
  const dhDataToShow = isUserPremium && !isUserExpired ? flattenedDhData : mockDhData;
  const [open, setOpen] = useState(false);

  const { rows } = useDataHubPreview(dhDataToShow);

  const columns = useMemo(
    () =>
      Array.from(new Set(dhDataToShow.map(generateSymbolKey))).map((columnKey, index) => {
        return {
          key: columnKey,
          displayComponent: (
            <ColumnHeader
              columnKey={columnKey}
              index={index}
              isSummary={isSummary}
              open={open}
              setOpen={setOpen}
            />
          )
        };
      }),
    [dhDataToShow, isSummary, open]
  );

  if (loading) return <Loader />;

  function fixedHeaderContent() {
    return (
      <TableRow>
        <TableCell
          sx={{
            width: 100,
            position: 'sticky',
            left: 0,
            backgroundColor: 'background.paper',
            verticalAlign: 'middle !important',
            zIndex: 1
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'flex-end',
              alignItems: 'center',
              px: '8px',
              backgroundColor: 'background.paper'
            }}
          >
            <ButtonBase
              onClick={() => {
                setOpen(o => !o);
              }}
            >
              <KeyboardArrowDown
                sx={{
                  transform: !open ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform cubic-bezier(0.5, 0, 0.5, 1) 0.1s'
                }}
              />
            </ButtonBase>
          </Box>
        </TableCell>
        {columns.map(column => (
          <TableCell
            key={column.key}
            align='left'
            sx={{ backgroundColor: 'background.paper', width: 320, p: 1, px: '8px', pt: '16px' }}
          >
            {column.displayComponent}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  const rowContent = (
    index: number,
    row: Record<
      string,
      string | Record<string, { value: string; PeriodAbs: string; FactsheetVersion: string }>
    >
  ) => {
    const rowStyle = { backgroundColor: index % 2 === 0 ? 'secondary.main' : 'background.paper' };

    return (
      <>
        <TableCell
          sx={{
            width: 'fit-content',
            whiteSpace: 'nowrap',
            position: 'sticky',
            left: 0,
            ...rowStyle,
            px: 2
          }}
        >
          <DateText dateString={row.date as string} />
        </TableCell>
        {columns.map(column => {
          const key = column.key.split(separator).slice(0, 5).join(separator);
          const cellData = (
            row.values as Record<
              string,
              {
                value: string;
                PeriodAbs: string;
                FactsheetVersion: string;
              }
            >
          )[key];

          return (
            <TableCell
              key={column.key}
              align='center'
              sx={rowStyle}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mx: '40%'
                }}
              >
                {cellData?.value ? (
                  <Tooltip
                    title={
                      <PreviewTooltipContent
                        code={column.key.split(separator)[3]}
                        factsheetVersion={cellData.FactsheetVersion || '-'}
                        period={column.key.split(separator)[1]}
                        periodAbs={cellData.PeriodAbs || '-'}
                        periodType={column.key.split(separator)[2]}
                        timeRef={column.key.split(separator)[0]}
                      />
                    }
                    variant='secondary'
                  >
                    {formatNumberWithPrecision(cellData?.value)}
                  </Tooltip>
                ) : (
                  '-'
                )}
              </Box>
            </TableCell>
          );
        })}
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', position: 'relative', flex: 1 }}>
      <Paper sx={{ flex: 1 }}>
        <TableVirtuoso
          components={VirtuosoTableComponents}
          data={rows}
          fixedHeaderContent={fixedHeaderContent}
          itemContent={(index, row) => rowContent(index, row)}
        />
      </Paper>
    </Box>
  );
};

export default DataHubPreview;
